import { useState, useRef, useEffect, useCallback } from 'react';
import { getBaseUrl } from '../../shared/api';

// Just enough to skip a truly empty recording
const MIN_BLOB_BYTES = 1000;

// WebM is the most reliable format in Chromium; use OGG/MP4 only where needed.
function getSupportedMimeType() {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

function mimeToExt(mime) {
  if (mime.startsWith('audio/ogg')) return 'ogg';
  if (mime.startsWith('audio/mp4')) return 'mp4';
  return 'webm';
}

// Firefox claims webm support but actually records ogg — detect from magic bytes
async function detectActualMime(blob) {
  const buf = await blob.slice(0, 12).arrayBuffer();
  const b = new Uint8Array(buf);
  if (b[0] === 0x4F && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) return 'audio/ogg';
  if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return 'audio/mp4';
  return 'audio/webm';
}

/**
 * Push-to-talk recording hook.
 * startRecording(onTranscript) opens the mic and registers a callback.
 * Recording stops on document pointerup; onTranscript(text) is called with the result.
 */
export default function useVoiceRecording({ language } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const onTranscriptRef = useRef(null);
  const stopRequestedRef = useRef(false);
  // Keep language current inside async callbacks without stale closure
  const languageRef = useRef(language);
  useEffect(() => { languageRef.current = language; }, [language]);

  useEffect(() => {
    return () => { streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const startRecording = useCallback(async (onTranscript) => {
    setError(null);
    stopRequestedRef.current = false;
    onTranscriptRef.current = onTranscript;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const activeMime = recorder.mimeType || mimeType || 'audio/webm';
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setIsRecording(false);

        const blob = new Blob(chunksRef.current, { type: activeMime });
        chunksRef.current = [];

        // Skip sending if audio is too short — model would hallucinate
        if (blob.size < MIN_BLOB_BYTES) {
          onTranscriptRef.current?.('');
          onTranscriptRef.current = null;
          return;
        }

        setIsTranscribing(true);
        try {
          const fd = new FormData();
          const actualMime = await detectActualMime(blob);
          const ext = mimeToExt(actualMime);
          // Re-wrap blob with correct type so FormData sends matching Content-Type
          const typedBlob = actualMime !== blob.type.split(';')[0] ? new Blob([blob], { type: actualMime }) : blob;
          fd.append('audio', typedBlob, `voice.${ext}`);
          const lang = languageRef.current;
          if (lang) fd.append('language', lang);

          const res = await fetch(`${getBaseUrl()}/assistant/api/transcribe/`, {
            method: 'POST',
            body: fd,
            credentials: 'include',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          onTranscriptRef.current?.(data.text || '');
        } catch (err) {
          setError(err.message);
          onTranscriptRef.current?.('');
        } finally {
          setIsTranscribing(false);
          onTranscriptRef.current = null;
        }
      };

      // Stop on pointerup anywhere in the document
      const onPointerUp = () => {
        document.removeEventListener('pointerup', onPointerUp);
        if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current.stop();
      };
      document.addEventListener('pointerup', onPointerUp);

      // Keep one recording session so the browser emits one complete container.
      recorder.start();
      setIsRecording(true);
      if (stopRequestedRef.current) recorder.stop();
    } catch (err) {
      setError(err.message);
      onTranscriptRef.current?.('');
      onTranscriptRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    stopRequestedRef.current = true;
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  return { isRecording, isTranscribing, error, startRecording, stopRecording };
}
